import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
} from "firebase/auth";
import { auth } from "./firebase";
import AbacusBoard from "./abacus/AbacusBoard";
import MentalMath from "./abacus/MentalMath";
import AbacusTest from "./abacus/AbacusTest";
import {
  emptyRods,
  numberToRods,
  rodsToNumber,
  randInt,
  maxValueForRods,
  formatNumber,
} from "./abacus/mathUtils";
import {
  loadAbacusProfile,
  saveAbacusSettings,
  persistProgress,
  foldTestResult,
  emptyStats,
  prettyAuthError,
} from "./abacus/abacusService";
import "./Abacus.css";

const SETTINGS_KEY = "abacus.settings.v2";

const DEFAULT_SETTINGS = {
  rodCount: 7,
  beadColorHeaven: "#f59e0b",
  beadColorEarth: "#38bdf8",
  frameColor: "#0f2942",
  rodColor: "#c8a878",
  barColor: "#0b3d2e",
  beadShape: "diamond",
  size: 1,
  showValues: true,
  showDigits: true,
  showTotal: true,
};

const THEMES = [
  { name: "Chalkboard", frameColor: "#0f2942", beadColorHeaven: "#f59e0b", beadColorEarth: "#38bdf8", rodColor: "#c8a878", barColor: "#0b3d2e" },
  { name: "Sakura", frameColor: "#4a2c3f", beadColorHeaven: "#fb7185", beadColorEarth: "#f9a8d4", rodColor: "#e9d5c0", barColor: "#7c2d4b" },
  { name: "Forest", frameColor: "#14342b", beadColorHeaven: "#a3e635", beadColorEarth: "#34d399", rodColor: "#d6c39a", barColor: "#052e21" },
  { name: "Grape", frameColor: "#2e1065", beadColorHeaven: "#c084fc", beadColorEarth: "#818cf8", rodColor: "#cbb892", barColor: "#4c1d95" },
  { name: "Ember", frameColor: "#431407", beadColorHeaven: "#fbbf24", beadColorEarth: "#f97316", rodColor: "#e0c39a", barColor: "#7c2d12" },
  { name: "Classic", frameColor: "#3a2417", beadColorHeaven: "#b45309", beadColorEarth: "#92400e", rodColor: "#d9b98c", barColor: "#1c1006" },
];

const SHAPES = [
  { id: "diamond", label: "◆ Soroban" },
  { id: "round", label: "● Round" },
  { id: "hex", label: "⬡ Hex" },
];

function readLocalSettings() {
  if (typeof window === "undefined") return { ...DEFAULT_SETTINGS };
  try {
    const raw = window.localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch (_) {
    return { ...DEFAULT_SETTINGS };
  }
}

function writeLocalSettings(settings) {
  try {
    window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch (_) {}
}

const MODES = [
  { id: "abacus", label: "Abacus", icon: "🧮" },
  { id: "mental", label: "Mental Math", icon: "🧠" },
  { id: "test", label: "Test", icon: "📝" },
  { id: "stats", label: "Progress", icon: "📊" },
];

export default function Abacus() {
  const [user, setUser] = useState(null);
  const [mode, setMode] = useState("abacus");
  const [settings, setSettings] = useState(readLocalSettings);
  const [rods, setRods] = useState(() => emptyRods(readLocalSettings().rodCount));
  const [stats, setStats] = useState(emptyStats());
  const [history, setHistory] = useState([]);
  const [authOpen, setAuthOpen] = useState(false);
  const [toast, setToast] = useState("");

  const settingsSaveTimer = useRef(null);
  const toastTimer = useRef(null);

  const total = useMemo(() => rodsToNumber(rods), [rods]);

  // ── Auth + cloud profile ──────────────────────────────────────────────
  useEffect(() => {
    return onAuthStateChanged(auth, async (nextUser) => {
      setUser(nextUser || null);
      if (nextUser) {
        setAuthOpen(false);
        const profile = await loadAbacusProfile(nextUser.uid);
        if (profile) {
          if (profile.settings) {
            setSettings((prev) => {
              const merged = { ...prev, ...profile.settings };
              writeLocalSettings(merged);
              return merged;
            });
          }
          if (profile.stats) setStats({ ...emptyStats(), ...profile.stats });
          if (Array.isArray(profile.history)) setHistory(profile.history);
        }
      } else {
        setStats(emptyStats());
        setHistory([]);
      }
    });
  }, []);

  // Keep the board sized to the rod count.
  useEffect(() => {
    setRods((prev) => {
      if (prev.length === settings.rodCount) return prev;
      const value = rodsToNumber(prev);
      const capped = Math.min(value, maxValueForRods(settings.rodCount));
      return numberToRods(capped, settings.rodCount);
    });
  }, [settings.rodCount]);

  // Persist settings locally instantly, to the cloud with a small debounce.
  useEffect(() => {
    writeLocalSettings(settings);
    if (!user) return;
    if (settingsSaveTimer.current) clearTimeout(settingsSaveTimer.current);
    settingsSaveTimer.current = setTimeout(() => {
      saveAbacusSettings(user.uid, settings);
    }, 900);
    return () => settingsSaveTimer.current && clearTimeout(settingsSaveTimer.current);
  }, [settings, user]);

  function showToast(message) {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 2600);
  }

  function updateSettings(patch) {
    setSettings((prev) => ({ ...prev, ...patch }));
  }

  function applyTheme(theme) {
    updateSettings({
      frameColor: theme.frameColor,
      beadColorHeaven: theme.beadColorHeaven,
      beadColorEarth: theme.beadColorEarth,
      rodColor: theme.rodColor,
      barColor: theme.barColor,
    });
  }

  function handleTestComplete(result) {
    const folded = foldTestResult(stats, history, result);
    setStats(folded.stats);
    setHistory(folded.history);
    if (user) {
      persistProgress(user.uid, { stats: folded.stats, history: folded.history });
      showToast(`Saved: ${folded.scorePct}% (${result.correct}/${result.total})`);
    } else {
      showToast("Sign in to save your progress across devices.");
    }
  }

  async function handleSignOut() {
    await signOut(auth);
    showToast("Signed out.");
  }

  return (
    <div className="abx-app">
      <MathBackground />

      <header className="abx-header">
        <div className="abx-header-left">
          <Link to="/" className="abx-home" aria-label="Back to VMCREATION home">←</Link>
          <div className="abx-brand">
            <span className="abx-brand-mark">🧮</span>
            <div className="abx-brand-text">
              <span className="abx-brand-title">Abacus<span className="abx-brand-accent">Lab</span></span>
              <span className="abx-brand-sub">Soroban · Mental Math · Tests</span>
            </div>
          </div>
        </div>

        <nav className="abx-tabs" aria-label="Modes">
          {MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              className={`abx-tab ${mode === m.id ? "is-on" : ""}`}
              onClick={() => setMode(m.id)}
            >
              <span className="abx-tab-icon">{m.icon}</span>
              <span className="abx-tab-label">{m.label}</span>
            </button>
          ))}
        </nav>

        <div className="abx-account">
          {user ? (
            <div className="abx-account-in">
              <span className="abx-avatar">{initials(user)}</span>
              <span className="abx-account-name">{displayName(user)}</span>
              <button type="button" className="abx-btn abx-btn--ghost" onClick={handleSignOut}>
                Log out
              </button>
            </div>
          ) : (
            <button type="button" className="abx-btn abx-btn--primary" onClick={() => setAuthOpen(true)}>
              Log in
            </button>
          )}
        </div>
      </header>

      <main className="abx-main">
        {mode === "abacus" && (
          <AbacusMode
            rods={rods}
            setRods={setRods}
            settings={settings}
            updateSettings={updateSettings}
            applyTheme={applyTheme}
            total={total}
            showToast={showToast}
          />
        )}
        {mode === "mental" && (
          <div className="abx-stack">
            <MentalMath settings={settings} />
          </div>
        )}
        {mode === "test" && (
          <div className="abx-stack">
            <AbacusTest rodCount={settings.rodCount} onComplete={handleTestComplete} />
          </div>
        )}
        {mode === "stats" && (
          <StatsMode user={user} stats={stats} history={history} onLogin={() => setAuthOpen(true)} />
        )}
      </main>

      {toast && <div className="abx-toast">{toast}</div>}
      {authOpen && !user && (
        <AuthModal onClose={() => setAuthOpen(false)} onToast={showToast} />
      )}
    </div>
  );
}

// ── Abacus (tool + customizer) ─────────────────────────────────────────────
function AbacusMode({ rods, setRods, settings, updateSettings, applyTheme, total, showToast }) {
  const [setValue, setSetValue] = useState("");

  function clear() {
    setRods(emptyRods(settings.rodCount));
  }
  function randomize() {
    const v = randInt(1, maxValueForRods(settings.rodCount));
    setRods(numberToRods(v, settings.rodCount));
  }
  function applySetValue(e) {
    e.preventDefault();
    const v = Math.max(0, Math.min(maxValueForRods(settings.rodCount), Math.floor(Number(setValue) || 0)));
    setRods(numberToRods(v, settings.rodCount));
    setSetValue("");
  }
  function bump(delta) {
    const v = Math.max(0, Math.min(maxValueForRods(settings.rodCount), total + delta));
    setRods(numberToRods(v, settings.rodCount));
  }

  return (
    <div className="abx-abacus-layout">
      <section className="abx-panel abx-board-panel">
        <div className="abx-board-scroll">
          <AbacusBoard rods={rods} settings={settings} onChange={setRods} interactive />
        </div>

        <div className="abx-board-controls">
          <button type="button" className="abx-btn" onClick={() => bump(-1)}>−1</button>
          <form className="abx-set-form" onSubmit={applySetValue}>
            <input
              type="number"
              className="abx-set-input"
              placeholder="Set a number…"
              value={setValue}
              onChange={(e) => setSetValue(e.target.value)}
            />
            <button type="submit" className="abx-btn abx-btn--primary">Set</button>
          </form>
          <button type="button" className="abx-btn" onClick={() => bump(1)}>+1</button>
          <button type="button" className="abx-btn" onClick={randomize}>🎲 Random</button>
          <button type="button" className="abx-btn abx-btn--ghost" onClick={clear}>Clear</button>
        </div>
      </section>

      <aside className="abx-panel abx-customizer">
        <h3 className="abx-panel-title">Customize your abacus</h3>

        <div className="abx-field">
          <label>Rods (columns): <b>{settings.rodCount}</b></label>
          <input type="range" min="3" max="15" value={settings.rodCount}
            onChange={(e) => updateSettings({ rodCount: Number(e.target.value) })} />
        </div>

        <div className="abx-field">
          <label>Bead size: <b>{Math.round(settings.size * 100)}%</b></label>
          <input type="range" min="0.7" max="1.5" step="0.05" value={settings.size}
            onChange={(e) => updateSettings({ size: Number(e.target.value) })} />
        </div>

        <div className="abx-field">
          <label>Bead shape</label>
          <div className="abx-chip-row">
            {SHAPES.map((sh) => (
              <button key={sh.id} type="button"
                className={`abx-chip ${settings.beadShape === sh.id ? "is-on" : ""}`}
                onClick={() => updateSettings({ beadShape: sh.id })}>{sh.label}</button>
            ))}
          </div>
        </div>

        <div className="abx-field">
          <label>Preset palettes</label>
          <div className="abx-theme-row">
            {THEMES.map((t) => (
              <button key={t.name} type="button" className="abx-theme-swatch" title={t.name}
                onClick={() => applyTheme(t)}
                style={{ background: t.frameColor }}>
                <span style={{ background: t.beadColorHeaven }} />
                <span style={{ background: t.beadColorEarth }} />
              </button>
            ))}
          </div>
        </div>

        <div className="abx-color-grid">
          <ColorField label="Heaven beads" value={settings.beadColorHeaven}
            onChange={(v) => updateSettings({ beadColorHeaven: v })} />
          <ColorField label="Earth beads" value={settings.beadColorEarth}
            onChange={(v) => updateSettings({ beadColorEarth: v })} />
          <ColorField label="Frame" value={settings.frameColor}
            onChange={(v) => updateSettings({ frameColor: v })} />
          <ColorField label="Reckoning bar" value={settings.barColor}
            onChange={(v) => updateSettings({ barColor: v })} />
          <ColorField label="Rods" value={settings.rodColor}
            onChange={(v) => updateSettings({ rodColor: v })} />
        </div>

        <div className="abx-toggle-col">
          <label className="abx-switch">
            <input type="checkbox" checked={settings.showValues}
              onChange={(e) => updateSettings({ showValues: e.target.checked })} />
            <span>Show place values (10ⁿ)</span>
          </label>
          <label className="abx-switch">
            <input type="checkbox" checked={settings.showDigits}
              onChange={(e) => updateSettings({ showDigits: e.target.checked })} />
            <span>Show digit under each rod</span>
          </label>
          <label className="abx-switch">
            <input type="checkbox" checked={settings.showTotal}
              onChange={(e) => updateSettings({ showTotal: e.target.checked })} />
            <span>Show running total (Σ)</span>
          </label>
        </div>

        <button type="button" className="abx-btn abx-btn--ghost abx-btn--wide"
          onClick={() => { updateSettings({ ...DEFAULT_SETTINGS }); showToast("Reset to defaults."); }}>
          Reset to defaults
        </button>
      </aside>
    </div>
  );
}

function ColorField({ label, value, onChange }) {
  return (
    <label className="abx-color-field">
      <span>{label}</span>
      <span className="abx-color-swatch" style={{ background: value }}>
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} />
      </span>
    </label>
  );
}

// ── Progress / stats ───────────────────────────────────────────────────────
function StatsMode({ user, stats, history, onLogin }) {
  const accuracy = stats.totalQuestions > 0
    ? Math.round((stats.totalCorrect / stats.totalQuestions) * 100)
    : 0;
  return (
    <div className="abx-stats">
      {!user && (
        <div className="abx-panel abx-signin-note">
          <span>🔒 You're practising as a guest. Progress is only saved on this device.</span>
          <button type="button" className="abx-btn abx-btn--primary" onClick={onLogin}>Log in to save</button>
        </div>
      )}

      <div className="abx-stat-cards">
        <StatCard label="Tests taken" value={stats.testsTaken} icon="📝" />
        <StatCard label="Questions" value={stats.totalQuestions} icon="❓" />
        <StatCard label="Accuracy" value={`${accuracy}%`} icon="🎯" />
        <StatCard label="Best score" value={`${stats.bestScorePct}%`} icon="🏆" />
        <StatCard label="Best streak" value={stats.bestStreak} icon="🔥" />
      </div>

      <div className="abx-panel abx-history">
        <h3 className="abx-panel-title">Recent tests</h3>
        {history.length === 0 ? (
          <div className="abx-empty">No tests yet — head to the <b>Test</b> tab to begin.</div>
        ) : (
          <div className="abx-history-list">
            {history.map((h) => (
              <div key={h.id} className="abx-history-row">
                <span className={`abx-history-score ${h.scorePct >= 80 ? "great" : h.scorePct >= 50 ? "ok" : "low"}`}>
                  {h.scorePct}%
                </span>
                <span className="abx-history-label">{h.label}</span>
                <span className="abx-history-frac">{h.correct}/{h.total}</span>
                <span className="abx-history-date">{timeAgo(h.at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="abx-stat-card">
      <span className="abx-stat-icon">{icon}</span>
      <span className="abx-stat-value">{typeof value === "number" ? formatNumber(value) : value}</span>
      <span className="abx-stat-label">{label}</span>
    </div>
  );
}

// ── Auth modal ─────────────────────────────────────────────────────────────
function AuthModal({ onClose, onToast }) {
  const [authMode, setAuthMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const cleanEmail = email.trim().toLowerCase();
      if (authMode === "login") {
        await signInWithEmailAndPassword(auth, cleanEmail, password);
        onToast("Welcome back!");
      } else {
        const cred = await createUserWithEmailAndPassword(auth, cleanEmail, password);
        const dn = name.trim() || cleanEmail.split("@")[0];
        await updateProfile(cred.user, { displayName: dn.slice(0, 60) });
        onToast("Account created — happy calculating!");
      }
      onClose();
    } catch (err) {
      setError(prettyAuthError(err));
      setBusy(false);
    }
  }

  return (
    <div className="abx-modal-backdrop" onMouseDown={onClose}>
      <div className="abx-modal" onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className="abx-modal-close" onClick={onClose} aria-label="Close">×</button>
        <div className="abx-modal-head">
          <span className="abx-brand-mark">🧮</span>
          <h2>{authMode === "login" ? "Welcome back" : "Create your account"}</h2>
          <p>Save custom abacuses, test scores, and streaks across devices.</p>
        </div>
        <form className="abx-auth-form" onSubmit={submit}>
          {authMode === "signup" && (
            <label>
              Name
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Your name" autoComplete="name" maxLength={60} />
            </label>
          )}
          <label>
            Email
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com" autoComplete="email" required />
          </label>
          <label>
            Password
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              autoComplete={authMode === "login" ? "current-password" : "new-password"}
              minLength={6} required />
          </label>
          {error && <div className="abx-auth-error">{error}</div>}
          <button type="submit" className="abx-btn abx-btn--primary abx-btn--wide" disabled={busy}>
            {busy ? "…" : authMode === "login" ? "Log in" : "Sign up"}
          </button>
        </form>
        <button type="button" className="abx-auth-swap"
          onClick={() => { setAuthMode(authMode === "login" ? "signup" : "login"); setError(""); }}>
          {authMode === "login" ? "Need an account? Sign up" : "Already have an account? Log in"}
        </button>
      </div>
    </div>
  );
}

// ── Animated math background ───────────────────────────────────────────────
function MathBackground() {
  const symbols = useMemo(() => {
    const glyphs = ["∑", "π", "√", "∞", "∫", "×", "÷", "±", "≈", "Δ", "θ", "φ", "%", "9", "5", "1", "0", "+", "−", "="];
    return Array.from({ length: 26 }, (_, i) => ({
      char: glyphs[i % glyphs.length],
      left: Math.random() * 100,
      delay: Math.random() * 18,
      duration: 16 + Math.random() * 16,
      size: 14 + Math.random() * 34,
      opacity: 0.05 + Math.random() * 0.09,
    }));
  }, []);
  return (
    <div className="abx-mathbg" aria-hidden="true">
      {symbols.map((s, i) => (
        <span key={i} className="abx-glyph" style={{
          left: `${s.left}%`,
          animationDelay: `${s.delay}s`,
          animationDuration: `${s.duration}s`,
          fontSize: `${s.size}px`,
          opacity: s.opacity,
        }}>{s.char}</span>
      ))}
    </div>
  );
}

// ── small helpers ──────────────────────────────────────────────────────────
function displayName(user) {
  if (!user) return "";
  return user.displayName || (user.email ? user.email.split("@")[0] : "You");
}
function initials(user) {
  const n = displayName(user);
  return n ? n.slice(0, 1).toUpperCase() : "?";
}
function timeAgo(ts) {
  const diff = Date.now() - (ts || 0);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
